import React, { useEffect, useState } from 'react';
import { getUserCases, getAppUser } from '../../functions/utils';
import {
  getAuditfileImports,
  deleteAuditfileImport,
  handleAuditfileImport,
  handleAuditfileFinal,
  resetAuditfileStatus,
} from '../../functions/auditfile';
import '../../css/ImportComponent.css';
import ImportHeaderComponent from '../page/ImportHeaderComponent';
import DropdownComponent from '../utilities/DropdownComponent';
import LoaderComponent from '../utilities/LoaderComponent';
import FileDropComponent from '../utilities/FileDropComponent';
import ConfirmationComponent from '../utilities/ConfirmationComponent';
import AuditfileLogo from '../../images/AuditfileLogo.png';
import { useTranslations } from '../utilities/TranslationContext';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark, faCircleCheck, faCircleMinus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

library.add(faXmark, faCircleCheck, faCircleMinus);

const AuditfileComponent = () => {
  const { getTranslation, language } = useTranslations();

  const [userCases, setUserCases] = useState([]);
  const [imports, setImports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedCase, setSelectedCase] = useState(false);
  const [periodMode, setPeriodMode] = useState('default');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingFilesMetadata, setPendingFilesMetadata] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);
  const [fileDropKey, setFileDropKey] = useState(0);

  const [confirmDeleteId, setConfirmDeleteId] = useState(false);
  const [journalSelections, setJournalSelections] = useState({});

  const showUploadForm = pendingFiles.length > 0;
  const canImport = showUploadForm && selectedCase && periodMode && !isUploading;

  useEffect(() => { init(); }, []);

  async function init() {
    const appUser = await getAppUser();
    if (!appUser) return setTimeout(init, 300);
    const cases = await getUserCases();
    if (cases) setUserCases(cases);
    const imps = await getAuditfileImports(cases);
    setImports(Array.isArray(imps) ? imps : []);
    setIsLoading(false);
  }

  async function reload() {
    const imps = await getAuditfileImports(userCases);
    setImports(Array.isArray(imps) ? imps : []);
  }

  // ── File handling ──────────────────────────────────────────────────────────────

  function extractFileMetadata(xml) {
    const companyMatch = xml.match(/<Name>([^<]+)<\/Name>/i);
    const fiscalYearMatch = xml.match(/<FiscalYear>([^<]+)<\/FiscalYear>/i);
    const softwareMatch = xml.match(/<SoftwareDesc>([^<]+)<\/SoftwareDesc>/i);
    const yearStartMatch = xml.match(/<YearStart>([^<]+)<\/YearStart>/i);
    const xafNsMatch = xml.match(/xmlns[^=]*=["'][^"']*\/XAF\/([^"'\/\s]+)/i);
    const swVersionMatch = xml.match(/<softwareVersion>([^<]+)<\/softwareVersion>/i);
    const journals = scanSpreadJournals(xml);
    return {
      company: companyMatch ? companyMatch[1].trim() : '',
      fiscalYear: fiscalYearMatch ? fiscalYearMatch[1].trim() : (yearStartMatch ? yearStartMatch[1].trim() : ''),
      software: softwareMatch ? softwareMatch[1].trim() : '',
      version: xafNsMatch ? xafNsMatch[1].trim() : (swVersionMatch ? swVersionMatch[1].trim() : ''),
      hasPeriodSpread: journals.length > 0,
      journals,
    };
  }

  function scanSpreadJournals(xml) {
    // Split on journal closing tags — one chunk per journal, no regex backtracking
    const closeTagMatch = xml.match(/<\/(Journal|journal)>/);
    if (!closeTagMatch) return [];
    const closeTag = `</${closeTagMatch[1]}>`;
    const blocks = xml.split(closeTag);
    const result = [];
    for (const block of blocks) {
      const idMatch = block.match(/<(?:JrnID|jrnID|journalID)>([^<]+)<\/(?:JrnID|jrnID|journalID)>/i);
      if (!idMatch) continue;
      const jrnId = idMatch[1].trim();
      // Only look for description in the journal header, before the first transaction block
      const txStart = block.search(/<(?:Transaction|transaction)\b/i);
      const header = txStart > 0 ? block.slice(0, txStart) : block;
      const descMatch = header.match(/<(?:JrnDesc|jrnDesc|journalDesc|description|Desc|desc)>([^<]+)<\/(?:JrnDesc|jrnDesc|journalDesc|description|Desc|desc)>/i);
      const jrnDesc = descMatch ? descMatch[1].trim() : '';
      // Flag journal only if a single transaction has lines spanning multiple effectiveDate months
      const txCloseTag = /\<\/transaction\>/i.test(block) ? '</transaction>' : '</Transaction>';
      const txBody = txStart > 0 ? block.slice(txStart) : block;
      const txBlocks = txBody.split(txCloseTag);
      let hasSpread = false;
      for (const tx of txBlocks) {
        const months = new Set();
        const efRe = /<(?:EfDate|effectiveDate)>(\d{4}-\d{2})/gi;
        let m;
        while ((m = efRe.exec(tx)) !== null) {
          months.add(m[1]);
          if (months.size > 1) { hasSpread = true; break; }
        }
        if (hasSpread) break;
      }
      if (hasSpread) result.push({ jrnId, jrnDesc });
    }
    return result;
  }

  async function onFilesDropped(newFiles) {
    if (newFiles.length === 0) return;
    const metaArr = [];
    for (const file of newFiles) {
      try {
        const xml = await file.text();
        metaArr.push(extractFileMetadata(xml));
      } catch {
        metaArr.push({ company: '', fiscalYear: '', software: '', hasPeriodSpread: false, journals: [] });
      }
    }
    setPendingFiles(prev => {
      const offset = prev.length;
      const newSel = {};
      metaArr.forEach((meta, i) => {
        if (meta.hasPeriodSpread) newSel[offset + i] = new Set(meta.journals.map(j => j.jrnId));
      });
      setJournalSelections(prev2 => ({ ...prev2, ...newSel }));
      return [...prev, ...newFiles];
    });
    setPendingFilesMetadata(prev => [...prev, ...metaArr]);
  }

  function cancelUpload() {
    setPendingFiles([]);
    setPendingFilesMetadata([]);
    setJournalSelections({});
    setSelectedCase(false);
    setPeriodMode('default');
    setDropdownKey(k => k + 1);
    setFileDropKey(k => k + 1);
  }

  function removePendingFile(idx) {
    const files = pendingFiles.filter((_, i) => i !== idx);
    const meta = pendingFilesMetadata.filter((_, i) => i !== idx);
    setPendingFiles(files);
    setPendingFilesMetadata(meta);
    setJournalSelections(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const n = parseInt(k);
        if (n < idx) next[n] = v;
        else if (n > idx) next[n - 1] = v;
      });
      return next;
    });
    setFileDropKey(k => k + 1);
    if (files.length === 0) setDropdownKey(k => k + 1);
  }

  function toggleJournal(fileIdx, jrnId) {
    setJournalSelections(prev => {
      const sel = new Set(prev[fileIdx] || []);
      if (sel.has(jrnId)) sel.delete(jrnId); else sel.add(jrnId);
      return { ...prev, [fileIdx]: sel };
    });
  }

  function toggleAllJournals(fileIdx, journalIds, selectAll) {
    setJournalSelections(prev => ({ ...prev, [fileIdx]: selectAll ? new Set(journalIds) : new Set() }));
  }

  // ── Import ─────────────────────────────────────────────────────────────────────

  async function runImport() {
    if (!selectedCase) return;
    setIsUploading(true);
    await resetAuditfileStatus();

    const entiteit = `${selectedCase}_1`;
    const impidSet = new Map();

    for (let fi = 0; fi < pendingFiles.length; fi++) {
      const file = pendingFiles[fi];
      const meta = pendingFilesMetadata[fi] || {};
      // null  → no journal filter, apply effectiveDate to all (no spread detected)
      // array → apply only to the journals the user selected
      const effectiveDateJournals = periodMode === 'effectivedate' && meta.hasPeriodSpread
        ? [...(journalSelections[fi] || [])]
        : null;
      try {
        const result = await handleAuditfileImport({ entiteit, fileName: file.name, file, periodMode, effectiveDateJournals });
        if (result?.configId) {
          impidSet.set(result.impid, { impid: result.impid, entiteit });
        }
      } catch (e) {
        console.error('Auditfile import error:', e);
      }
    }

    const finalEntries = Array.from(impidSet.values());
    if (finalEntries.length > 0) {
      try { await handleAuditfileFinal(finalEntries); } catch (e) {}
    }

    setIsUploading(false);
    cancelUpload();
    await reload();
  }

  // ── Delete ─────────────────────────────────────────────────────────────────────

  async function handleDelete(id) {
    await deleteAuditfileImport(id);
    setConfirmDeleteId(false);
    await reload();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  function getCaseName(entiteit) {
    const id = entiteit.substring(0, entiteit.indexOf('_'));
    const c = userCases.find(uc => String(uc.id) === String(id));
    return c ? c.name : entiteit;
  }

  function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function formatStamp(stamp) {
    if (!stamp) return '-';
    return new Date(stamp).toLocaleString(`${language?.language_code}-${language?.country_code}`, {
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    });
  }

  return (
    <div className="import-container">
      <ImportHeaderComponent
        title="Auditfile"
        logo={AuditfileLogo}
        subtitle={getTranslation('import_AuditfileSubtitle')}
      />
      <div className="import-component-container">
        <div className="import-component-content">
          <div className="import-content">

            {/* ── Upload section – always visible ── */}
            <div className="import-create-basic">
              <FileDropComponent key={fileDropKey} onFilesAccepted={onFilesDropped} showFileList={false} />
            </div>

            {/* ── Toggled inputs – only when a file is dropped ── */}
            {showUploadForm && !isUploading && (
              <div className="import-create-basic">
                <div className="case-content">
                  <p>{getTranslation('import_FVCase')}*</p>
                  <DropdownComponent
                    key={dropdownKey}
                    options={userCases.map(c => ({ label: c.name, value: c.id, type: 'case' }))}
                    defaultValue={{ label: getTranslation('import_SelectCase'), value: null, type: null }}
                    onSelect={o => o.type === 'case' && setSelectedCase(o.value)}
                    className="case"
                  />
                </div>
                <div className="case-content">
                  <p>Periodemethode*</p>
                  <DropdownComponent
                    key={`pm-${dropdownKey}`}
                    options={[
                      { label: 'Standaard periode', value: 'default', type: 'periodmode' },
                      { label: 'Effectieve datum', value: 'effectivedate', type: 'periodmode' },
                    ]}
                    defaultValue={{ label: 'Standaard periode', value: 'default', type: 'periodmode' }}
                    onSelect={o => o.type === 'periodmode' && setPeriodMode(o.value)}
                    className="periodmode"
                  />
                </div>
              </div>
            )}

            {/* ── Pending files list ── */}
            {showUploadForm && !isUploading && pendingFiles.length > 0 && (
              <div className="import-results-wrapper">
                <table className="import-results-table">
                  <thead>
                    <tr>
                      <th>Bestandsnaam</th>
                      <th>Versie</th>
                      <th>Boekjaar</th>
                      <th>Bedrijf</th>
                      <th>Software</th>
                      <th>Grootte</th>
                      {periodMode === 'effectivedate' && <th>Periodeverspreiding</th>}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingFiles.map((f, i) => {
                      const meta = pendingFilesMetadata[i] || {};
                      return (
                        <tr key={i} className="file-row-pending">
                          <td>{f.name}</td>
                          <td>{meta.version || '-'}</td>
                          <td>{meta.fiscalYear || '-'}</td>
                          <td>{meta.company || '-'}</td>
                          <td>{meta.software || '-'}</td>
                          <td>{formatSize(f.size)}</td>
                          {periodMode === 'effectivedate' && (
                            <td>
                              {meta.hasPeriodSpread
                                ? <span className="spread-detected"><FontAwesomeIcon icon={faCircleCheck} /> Meerdere periodes gevonden</span>
                                : <span className="spread-none"><FontAwesomeIcon icon={faCircleMinus} /> Één periode</span>
                              }
                            </td>
                          )}
                          <td>
                            <button className="file-drop-item-remove" onClick={() => removePendingFile(i)} type="button">
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Journal selection (effectivedate mode only) ── */}
            {periodMode === 'effectivedate' && !isUploading && pendingFilesMetadata.some(m => m.hasPeriodSpread) && (
              <div className="journal-selection-panel">
                <p className="journal-selection-title">Dagboeken met periodeverspreiding</p>
                <p className="journal-selection-hint">Selecteer voor welke dagboeken de effectieve datum de periode bepaalt:</p>
                {pendingFilesMetadata.map((meta, i) => {
                  if (!meta.hasPeriodSpread || !meta.journals?.length) return null;
                  const sel = journalSelections[i] || new Set();
                  const jrnIds = meta.journals.map(j => j.jrnId);
                  const allSelected = jrnIds.every(id => sel.has(id));
                  return (
                    <div key={i} className="journal-checkboxes">
                      <label className="journal-checkbox-label journal-checkbox-all">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => toggleAllJournals(i, jrnIds, !allSelected)}
                        />
                        <strong>Alle dagboeken</strong>
                      </label>
                      {meta.journals.map(({ jrnId, jrnDesc }) => (
                        <label key={jrnId} className="journal-checkbox-label">
                          <input
                            type="checkbox"
                            checked={sel.has(jrnId)}
                            onChange={() => toggleJournal(i, jrnId)}
                          />
                          <span className="jrn-id">{jrnId}</span>
                          {jrnDesc && <span className="jrn-desc">{jrnDesc}</span>}
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Upload loader ── */}
            {isUploading && <LoaderComponent isLoading paddingTop="10px" />}

            {/* ── Menubar ── */}
            {showUploadForm && !isUploading && (
              <div className="import-menubar">
                <button className={`import-btn submit-btn hover${!canImport ? ' disabled' : ''}`} disabled={!canImport} onClick={runImport}>
                  {getTranslation('import_Import')}
                </button>
                <button className="import-btn cancel-btn hover" onClick={cancelUpload}>
                  {getTranslation('import_Cancel')}
                </button>
              </div>
            )}

            {/* ── Import list – always visible ── */}
            <LoaderComponent isLoading={isLoading} paddingTop="20px" />
            {!isLoading && imports.length > 0 && (
              <div className="list-content">
                <table border="1">
                  <tbody>
                    <tr className="list-header">
                      <td className="list-case">{getTranslation('import_Case')}</td>
                      <td className="list-year">{getTranslation('import_Year')}</td>
                      <td>Bestandsnaam</td>
                      <td>Versie</td>
                      <td className="list-stamp no-wrap">{getTranslation('import_LastUpdated')}</td>
                      <td className="edit"></td>
                    </tr>
                    {imports.map((imp, index) => (
                      <tr key={imp.id} value={index}>
                        <td>{getCaseName(imp.entiteit)}</td>
                        <td>{imp.year}</td>
                        <td>{imp.auditfile_name || '-'}</td>
                        <td>{imp.version || '-'}</td>
                        <td>{formatStamp(imp.stamp)}</td>
                        <td className="td-editor">
                          <div className="delete-list list-editor-option hover" onClick={() => setConfirmDeleteId(imp.id)}>
                            <FontAwesomeIcon icon={faXmark} />
                          </div>
                          {confirmDeleteId === imp.id && (
                            <ConfirmationComponent
                              message="Weet u zeker dat u deze importconfiguratie wilt verwijderen?"
                              onCancel={() => setConfirmDeleteId(false)}
                              onConfirm={() => handleDelete(imp.id)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditfileComponent;
